<script setup lang="ts">
    import { computed } from 'vue';
    import { Bar } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { chartColor } from '@shared/modules/chart-colors';
    import type { IssueDailyStatistics } from '../../interfaces';
    import { STATISTICS_EVENT_TYPES, getStatisticsLabelMap } from './statistics-types';

    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
    applyChartDefaults();

    const props = defineProps<{
        eventsOverTime: IssueDailyStatistics[];
    }>();

    const emit = defineEmits<{
        (e: 'clickBar', contactType: string, date: string): void;
    }>();

    const labelMap = getStatisticsLabelMap();

    const colorMap = Object.fromEntries(STATISTICS_EVENT_TYPES.map((t) => [t.key, t.color]));
    const typeToContactType = Object.fromEntries(STATISTICS_EVENT_TYPES.map((t) => [t.key, t.contactType]));

    const dates = computed(() => {
        if (!props.eventsOverTime.length) return [];
        const dateSet = new Set<string>();
        props.eventsOverTime.forEach((series) => {
            series.Values.forEach((v) => dateSet.add(String(v.Date).substring(0, 10)));
        });
        return Array.from(dateSet).sort();
    });

    const chartData = computed(() => {
        if (!dates.value.length) return { labels: [], datasets: [] };

        const labels = dates.value.map((d) =>
            new Date(d + 'T12:00:00').toLocaleDateString(navigator.language, { month: '2-digit', day: '2-digit', year: 'numeric' })
        );

        const datasets = props.eventsOverTime.map((series) => {
            const key = series.TypeName.toLowerCase();
            const valueMap = new Map(series.Values.map((v) => [String(v.Date).substring(0, 10), v.Count]));
            const label = labelMap[key] || series.TypeName;
            return {
                label,
                data: dates.value.map((d) => valueMap.get(d) ?? 0),
                backgroundColor: chartColor(colorMap[key] ?? 'fallback'),
            };
        });

        return { labels, datasets };
    });

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_event: unknown, elements: { datasetIndex: number; index: number }[]) => {
            if (!elements.length) return;
            const key = props.eventsOverTime[elements[0].datasetIndex]?.TypeName?.toLowerCase();
            const contactType = key ? (typeToContactType[key] ?? null) : null;
            const date = dates.value[elements[0].index];
            if (contactType && date) emit('clickBar', contactType, date);
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { maxRotation: 0, font: { weight: 'bold' as const } },
            },
            y: {
                suggestedMin: 0,
                grid: { color: chartColor('grid') },
                ticks: {
                    callback: (value: number | string) => {
                        if (typeof value === 'number' && Math.floor(value) === value) return value;
                        return '';
                    },
                },
            },
        },
    };
</script>

<template>
    <div class="print-block">
        <h2 class="h3 m-xs-b">
            {{ $Resources.IssueStatisticsOverTime }}
            <button
                type="button"
                class="popover-trigger popover-trigger-lg"
                v-popover-info="{ heading: $Resources.IssueStatisticsOverTimeHeader, body: $Resources.IssueStatisticsOverTimeIntroduction }">
                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
            </button>
        </h2>
        <p>{{ $Resources.IssueStatisticsOverTimeDescription }}</p>
        <div class="chart-container chart-container-panel h-400">
            <Bar :data="chartData" :options="chartOptions" />
        </div>
    </div>
</template>

